import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import flatMap from 'lodash/flatMap';

import { SortDirection } from '@cast/design-system';

import { apiClient } from '@cast/infrastructure-api';

const CLUSTERS_EFFICIENCY_BY_CLUSTER_QUERY_KEY =
  'clusters-efficiency-by-cluster';

const getClustersEfficiencyByClusterQueryKey = ({
  startTime,
  endTime,
  useListingPrices,
  pageLimit,
  sortBy,
  sortDirection,
}: Partial<UseClustersEfficiencyByClusterQueryArgs> = {}) =>
  [
    CLUSTERS_EFFICIENCY_BY_CLUSTER_QUERY_KEY,
    startTime,
    endTime,
    useListingPrices ?? false,
    pageLimit,
    sortBy,
    sortDirection,
  ] as const;

export type UseClustersEfficiencyByClusterQueryArgs = {
  startTime: string;
  endTime: string;
  useListingPrices: boolean;
  pageLimit?: number;
  sortBy?: string;
  sortDirection?: SortDirection;
  enabled?: boolean;
};

const getClustersEfficiencyByClusterQueryOptions = ({
  startTime,
  endTime,
  sortBy,
  sortDirection,
  useListingPrices,
  pageLimit = 50,
  enabled = true,
}: UseClustersEfficiencyByClusterQueryArgs) =>
  infiniteQueryOptions({
    queryKey: getClustersEfficiencyByClusterQueryKey({
      startTime,
      endTime,
      useListingPrices,
      pageLimit,
      sortBy,
      sortDirection,
    }),
    queryFn: async ({ pageParam }) => {
      const { data } =
        await apiClient.costReport.getOrganizationClustersEfficiencyByCluster({
          startTime,
          endTime,
          pageLimit: String(pageLimit),
          pageCursor: pageParam,
          sortField: sortBy,
          sortOrder: sortDirection,
          useListingPrices,
        });

      return data;
    },
    enabled,
    initialPageParam: '',
    getNextPageParam: (lastPage) => {
      return lastPage.nextCursor || undefined;
    },
  });

export const useClustersEfficiencyByClusterQuery = (
  args: UseClustersEfficiencyByClusterQueryArgs
) => {
  const {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isFetching,
  } = useInfiniteQuery(getClustersEfficiencyByClusterQueryOptions(args));

  return {
    isLoading,
    data: flatMap(data?.pages, (page) => page?.items ?? []),
    error,
    refetch,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isFetching,
    count: Number(data?.pages[0]?.count ?? 0),
  };
};
